import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { downtimeEvents, type DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import type { CauseCategoryId } from '../maintenance/downtime.seed';
import { CausesTab } from './Causes';
import { ReliabilityTab } from './Reliability';
import { PlannedRatioTab } from './PlannedRatio';
import { ShiftCorrelationTab } from './ShiftCorrelation';
import { ValueLostTab } from './ValueLost';
import { DowntimeDrill } from './DowntimeDrill';
import { DOWNTIME_TABS, downtimeTabHeadlines, isDowntimeTab, resolveDowntimeCause, type DowntimeTab } from './Scene.transform';
import { DowntimeImpactBand, DowntimeWindowSegment } from './DowntimeImpact';
import { SYN_CHIP } from './DowntimeCard';
import {
  downtimeImpactRead,
  downtimeWindow,
  downtimeWindowRows,
  isDowntimeWindowId,
  type DowntimeWindowId,
} from './DowntimeImpact.transform';

const HELP_DOC: Record<DowntimeTab, string> = {
  pareto: 'downtime/impact',
  mtbf: 'downtime/impact',
  ratio: 'downtime/impact',
  shift: 'downtime/impact',
  value: 'downtime/impact',
};

export function DowntimeScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = projectTileBySlug(projectId);
  const [tab, setTab] = useState<DowntimeTab>('pareto');
  const [windowId, setWindowId] = useState<DowntimeWindowId>('ytd');
  const allRows = downtimeEvents();
  const selectedRows: readonly DowntimeEvent[] = downtimeWindowRows(allRows, windowId);

  const causeParam = searchParams.get('cause');
  const selectedCause = resolveDowntimeCause(selectedRows, causeParam);
  const impactRead = downtimeImpactRead(allRows, windowId);
  const yearToDateImpactRead = windowId === 'ytd' ? impactRead : downtimeImpactRead(allRows, 'ytd');
  const headlines = downtimeTabHeadlines(selectedRows, allRows);
  const tabItems = DOWNTIME_TABS.map((item) => ({ ...item, count: headlines[item.id] }));

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (isDowntimeTab(tabParam)) setTab(tabParam);
    const windowParam = searchParams.get('window');
    setWindowId(isDowntimeWindowId(windowParam) ? windowParam : 'ytd');
  }, [searchParams]);

  useEffect(() => {
    document.title = project ? `Downtime · ${project.name}` : 'Downtime · OptiFlow';
  }, [project]);

  const selectTab = (value: string) => {
    if (!isDowntimeTab(value)) return;
    setTab(value);
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const selectWindow = (value: DowntimeWindowId) => {
    const next = new URLSearchParams(searchParams);
    next.set('window', value);
    next.delete('cause');
    setSearchParams(next, { replace: true });
  };

  const openCause = (nextTab: 'pareto' | 'value', causeCategory: CauseCategoryId) => {
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    next.set('cause', causeCategory);
    setSearchParams(next, { replace: true });
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody><SurfacePageHeader title="Plant not found" /></SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="ProjectDowntimeScene">
        <SurfacePageBody className="min-h-[calc(100vh-3.2rem)] space-y-3" width="full">
          <SurfacePageHeader
            title="Downtime"
            titleSize="large"
            actions={
              <>
                <DowntimeWindowSegment windowId={windowId} onWindowChange={selectWindow} />
                {SYN_CHIP}
                <HelpAnchor doc={HELP_DOC[tab]} label="Downtime" />
              </>
            }
          />
          <DowntimeImpactBand
            read={impactRead}
            yearToDateRead={yearToDateImpactRead}
            onOpenCause={openCause}
          />
          <SurfacePageTabs aria-label="Downtime" items={tabItems} value={tab} onChange={selectTab} variant="ink" className="pt-3" />
          {tab === 'pareto' ? <><CausesTab rows={selectedRows} impactRead={impactRead} selectedCause={selectedCause} /><DowntimeDrill /></> : null}
          {tab === 'mtbf' ? <ReliabilityTab /> : null}
          {tab === 'ratio' ? <PlannedRatioTab /> : null}
          {tab === 'shift' ? <ShiftCorrelationTab /> : null}
          {tab === 'value' ? <ValueLostTab rows={selectedRows} windowLabel={downtimeWindow(windowId).rangeLabel} windowFrom={downtimeWindow(windowId).from} windowTo={downtimeWindow(windowId).to} selectedCause={selectedCause} /> : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
